// src/components/resources/glossary/GlossaryHero.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'motion/react'
import RiBookOpenLine from '~/icons/remix/generated/RiBookOpenLine'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { GLOSSARY_CATEGORIES } from '~/lib/resources/types/shared/enums'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'
import { rankGlossaryTerms } from '~/lib/resources/glossary/search'
import { getGlossaryCategoryTheme } from '~/lib/resources/glossary/category-ui'
import { glossaryTermHref } from '~/lib/resources/resource-path'

type TermCardShape = Pick<
  GlossaryTerm,
  'slug' | 'title' | 'shortDescription' | 'category' | 'relatedTerms' | 'definition' | 'acronym'
>

interface GlossaryHeroProps {
  terms: TermCardShape[]
}

interface GlossaryResponse {
  data?: TermCardShape[]
}

export default function GlossaryHero({ terms: initialTerms }: GlossaryHeroProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [hasInteractedWithSearch, setHasInteractedWithSearch] = useState(false)
  const router = useRouter()

  const {
    data: completeGlossary,
    isFetching: isLoadingAllTerms,
    isSuccess: hasFetchedAllTerms,
    isError: isGlossaryError,
    error: glossaryError
  } = useQuery<GlossaryResponse>({
    queryKey: ['glossary', 'all-terms'],
    queryFn: async ({ signal }) => {
      const response = await fetch('/api/glossary', { signal })
      if (!response.ok) {
        throw new Error('Failed to fetch glossary terms')
      }
      return response.json() as Promise<GlossaryResponse>
    },
    enabled: hasInteractedWithSearch,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    retry: 1
  })

  const allTerms = useMemo(() => {
    const baseTerms = initialTerms ?? []
    const fetchedTerms = completeGlossary?.data ?? []
    if (!fetchedTerms.length) {
      return baseTerms
    }

    const existingSlugs = new Set(baseTerms.map(term => term.slug))
    const deduped = fetchedTerms.filter(term => !existingSlugs.has(term.slug))
    return [...baseTerms, ...deduped]
  }, [initialTerms, completeGlossary?.data])

  useEffect(() => {
    if (isGlossaryError) {
      logClientError(glossaryError, { component: 'GlossaryHero', context: 'Failed to fetch additional glossary terms' })
    }
  }, [isGlossaryError, glossaryError])

  /**
   * `useDeferredValue` lets keystrokes update the input synchronously while the
   * expensive scoring pass runs at a lower priority. This keeps INP under
   * 200ms on mobile devices where the synchronous filter alone could otherwise
   * exceed that budget.
   */
  const deferredQuery = useDeferredValue(searchQuery)
  const isSearching = deferredQuery !== searchQuery

  // Compute the results and any failure together as a PURE value; the failure is
  // logged from the effect below — logClientError POSTs to /api/log and must not
  // run in the render path (it would re-fire on every re-render / StrictMode
  // double-invoke for the same bad data).
  const { searchResults, searchError } = useMemo(() => {
    const trimmed = deferredQuery.trim()
    if (!trimmed) return { searchResults: [], searchError: null as unknown }
    const dataset = hasFetchedAllTerms ? allTerms : initialTerms
    try {
      // Shared 14-tier ranker (SoT: `~/lib/resources/glossary/search`); the hero
      // caps the dropdown at the top 8 matches.
      return { searchResults: rankGlossaryTerms(dataset, trimmed).slice(0, 8), searchError: null as unknown }
    } catch (error) {
      return { searchResults: [], searchError: error }
    }
  }, [deferredQuery, allTerms, initialTerms, hasFetchedAllTerms])

  useEffect(() => {
    if (searchError != null) {
      logClientError(searchError, { component: 'GlossaryHero', context: 'Error searching terms' })
    }
  }, [searchError])

  const handleSearchFocus = useCallback((): void => {
    if (!hasInteractedWithSearch) {
      setHasInteractedWithSearch(true)
    }
  }, [hasInteractedWithSearch])

  const handleSearchChange = useCallback((value: string): void => {
    if (!hasInteractedWithSearch) {
      setHasInteractedWithSearch(true)
    }
    setSearchQuery(value)
  }, [hasInteractedWithSearch])

  const handleSearchSelect = (term: TermCardShape): void => {
    router.push(glossaryTermHref(term))
    setSearchQuery('')
  }

  return (
    <div className="relative isolate -mt-[120px] overflow-hidden bg-gradient-to-b from-indigo-100/20 via-white to-white dark:from-indigo-900/20 dark:via-gray-950 dark:to-gray-950">
      {/* Background Pattern */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white/0 dark:bg-gray-950/0 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]" />
      <div className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl" aria-hidden="true">
        <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
      </div>

      <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-2xl lg:max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-center mb-8">
              <div className="rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 p-3">
                <RiBookOpenLine className="h-10 w-10 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
            <div className="relative z-0">
              <h1 className="text-4xl font-bold tracking-tight leading-[1.2] text-gray-900 dark:text-white sm:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 pb-1">
                Marketing Glossary
              </h1>
            </div>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Your comprehensive guide to marketing metrics, creative terminology, and platform-specific concepts.
              Master the language of modern marketing.
            </p>
          </motion.div>
          
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-10 mb-16"
          >
            <div className="relative max-w-xl mx-auto">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <RiSearchLine className="h-5 w-5 text-gray-400" />
              </div>
              {isLoadingAllTerms && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
                </div>
              )}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={handleSearchFocus}
                className="block w-full rounded-xl border-2 border-gray-200 bg-white/50 py-4 pl-11 pr-4 
                         text-gray-900 placeholder:text-gray-500 focus:bg-white focus:outline-none 
                         focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                         dark:border-gray-800 dark:bg-gray-900/50 dark:text-white 
                         dark:placeholder:text-gray-400 dark:focus:bg-gray-900
                         shadow-sm"
                placeholder="Search metrics, terms, platforms..."
              />

              {/* Search Results Dropdown */}
              {(searchResults.length > 0 || (isSearching && searchQuery.trim())) && (
                <div className="absolute mt-2 w-full rounded-xl border-2 border-gray-200 bg-white 
                             dark:border-gray-800 dark:bg-gray-900 shadow-lg z-50 max-h-96 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      Searching...
                    </div>
                  ) : (
                    <ul className="py-2">
                      {searchResults.map((term) => (
                        <li key={`${term.slug}-${term.category}`}>
                          <button
                            onClick={() => handleSearchSelect(term)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50
                                     flex items-start gap-3 group"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white group-hover:text-indigo-600 
                                          dark:group-hover:text-indigo-400">
                                {term.title}
                                {term.acronym && (
                                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                                    ({term.acronym})
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                {term.shortDescription}
                              </div>
                            </div>
                            <div className="mt-1 text-xs font-medium text-gray-400 dark:text-gray-500 capitalize">
                              {term.category}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Category Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-8"
          >
            {/* Category cards deep-link to the dedicated category pages
                (which carry the full term lists and per-category A–Z
                indexes) rather than in-page anchors, and include all five
                categories — the previous slice(0, 4) silently dropped
                Charts & Visualizations. */}
            {GLOSSARY_CATEGORIES.map((category) => {
              const Icon = getGlossaryCategoryTheme(category.id).icon
              return (
                <Link
                  key={category.id}
                  href={`/resources/glossary/${category.id}`}
                  className="group relative flex flex-col items-center p-4 rounded-xl bg-white/50 
                           dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-800
                           hover:border-indigo-200 dark:hover:border-indigo-900 transition-all
                           hover:shadow-md hover:-translate-y-1"
                >
                  <div className="rounded-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10 
                                p-2 group-hover:from-indigo-500/20 group-hover:to-purple-500/20 transition-all">
                    <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
                    {category.label}
                  </h2>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {category.description}
                  </span>
                </Link>
              )
            })}
          </motion.div>
        </div>
      </div>
    </div>
  )
} 
